import {
  Box,
  BoxProps,
  Group,
  Stack,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core"
import {
  IconThumbUp,
  IconAlertTriangle,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react"
import { ReactNode, useState } from "react"

export type InlineBannerProps = {
  type: "error" | "info" | "success"
  message?: string | ReactNode
  children?: ReactNode
  closable?: boolean
  size?: "xs" | "sm" | "md" | "lg" | string
  tooltip?: string
} & BoxProps

export function InlineBanner({
  type,
  message,
  children,
  closable = true,
  size = "sm",
  tooltip,
  ...props
}: InlineBannerProps) {
  const theme = useMantineTheme()
  const [open, setOpen] = useState<boolean>(true)

  const getTypeStyles = () => {
    switch (type) {
      case "error":
        return {
          bgColor: theme.colors.red[0],
          color: theme.colors.red[9],
          Icon: IconAlertTriangle,
        }
      case "success":
        return {
          bgColor: theme.colors.green[0],
          color: theme.colors.green[9],
          Icon: IconThumbUp,
        }
      case "info":
      default:
        return {
          bgColor: theme.colors.blue[0],
          color: theme.colors.blue[9],
          Icon: IconInfoCircle,
        }
    }
  }

  const { bgColor, color, Icon } = getTypeStyles()

  if (!open) {
    return null
  }

  return (
    <Box pos="relative" {...props}>
      {(() => {
        const stackContent = (
          <Stack
            mih={20}
            bg={bgColor}
            p="xs"
            fz={size}
            color={color}
            style={{ borderRadius: "5px" }}
            justify="space-between"
          >
            {message ? (
              <Group wrap="nowrap" align="center" gap={0}>
                <Icon size={15} color={color} style={{ marginRight: "5px" }} />
                {typeof message === "string" ? (
                  <Title c={color} order={6} fz={size}>
                    {message}
                  </Title>
                ) : (
                  message
                )}
              </Group>
            ) : null}
            {closable ? (
              <Box
                w={10}
                pos="absolute"
                right={0}
                px="xs"
                mr="sm"
                onClick={() => setOpen(false)}
                style={{ cursor: "pointer" }}
              >
                <IconX size={15} color={theme.colors.gray[4]} />
              </Box>
            ) : null}
            {children}
          </Stack>
        )

        return tooltip ? (
          <Tooltip label={tooltip}>{stackContent}</Tooltip>
        ) : (
          stackContent
        )
      })()}
    </Box>
  )
}
